/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the table component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */




import { alpha, styled } from '@mui/material';
import { DataGridPremium, DataGridPremiumProps, GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, gridClasses } from '@mui/x-data-grid-premium';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

export interface ITableProps<Type extends object = any> extends DataGridPremiumProps<Type> {
}

const ODD_OPACITY = 0.2;
const EVEN_OPACITY = 0.1;

const StripedDataGrid = styled(DataGridPremium)(({ theme }) => ({
    [`& .${gridClasses.toolbarContainer}`]: {
        backgroundColor: theme.palette.grey[200],
        borderBottom: "1px solid " + theme.palette.grey[400]
    },

    [`& .${gridClasses['container--bottom']}`]: {
        [`& .${gridClasses['pinnedRows--bottom']}`]: {
            [`& .${gridClasses['row--lastVisible']}`]: {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity
                ),
            }
        }
    },

    [`& .${gridClasses.row}.odd`]: {
        backgroundColor: theme.palette.grey[200],
        cursor: 'pointer',

        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },

        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.common.white,
        cursor: 'pointer',

        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, EVEN_OPACITY),
                '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },

        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                EVEN_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    EVEN_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        EVEN_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },

    [`& .${gridClasses.columnHeader}`]: {
        '&:focus': {
            outline: 'none'
        },

        '&:focus-within': {
            outline: 'none'
        },

        [`& .${gridClasses.aggregationColumnHeaderLabel}`]: {
            display: 'none'
        }
    },

    [`& .${gridClasses.cell}`]: {
        '&:focus': {
            outline: 'none'
        },

        '&:focus-within': {
            outline: 'none'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const TablePremium = <Type extends object>({
    ...tableProps
}: ITableProps<Type>) => {
    return (
        <StripedDataGrid
            disableColumnSelector
            disableRowSelectionOnClick
            disableColumnResize
            disableColumnMenu
            disable
            //@ts-expect-error
            getRowClassName={(params) =>
                params.id == GRID_AGGREGATION_ROOT_FOOTER_ROW_ID 
                ? ""
                : params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            {...tableProps}
        />
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default TablePremium;
